/**
 * AppPostureTab (formerly AppPermissionsTab): the OAuth app inventory in the
 * Carbon Security Center layout (mockup 03 patterns):
 *   brief        the pillar brief from `composeAppPostureBrief`, beside an
 *                inventory tile (the app count and the risk tiers as a
 *                severity meter) in place of a score tile: App Posture is
 *                inventory, not a Guardian score
 *   top three    the three apps to review first, as tiles in sequence
 *   permissions  the operation-by-scope heatmap beside publisher trust and
 *                admin consent
 *   consents     the latest consent grants as a structured list
 *   guide        the dismissible OAuth posture guide
 *   inventory    the App inventory DataTable: risk-tier contained tabs with
 *                counts, search and a filter menu, server pagination, and a
 *                "View details for {app}" button per row
 *   detail       the app detail side panel
 *
 * All hard preserves carry over: the acknowledgment workflow, the severity /
 * verified / status / review filters, pagination, the detail panel and the
 * `?app=<id>` deep link from the Message Center.
 */

import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useQuery,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query'
import { useMsal } from '@azure/msal-react'
import toast from 'react-hot-toast'

import { api } from '@/lib/api'
import PillarBrief from '@/components/PostureBrief/PillarBrief'
import { SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import TierStackedBar from '@/components/PostureBrief/TierStackedBar'
import EditorialHeatmap, {
  type HeatmapRow,
  type Intensity,
} from '@/components/PostureBrief/EditorialHeatmap'
import EmptyState from '@/components/shared/EmptyState'
import {
  composeAppPostureBrief,
  whyItMatters,
} from '@/lib/security-center/app-posture-brief'
import { BigNumber } from '@/components/carbon/BigNumber'
import { Button } from '@/components/carbon/Button'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { IconArrowRight, IconClose, IconInfo } from '@/components/carbon/icons'
import { IconButton } from '@/components/carbon/IconButton'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Marked } from '@/components/carbon/Marked'
import { SegmentedMeter } from '@/components/carbon/SegmentedMeter'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { severityFromApi } from '@/components/carbon/severity'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Tag } from '@/components/carbon/Tag'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'

import { AppPermissionsSummaryCards } from './AppPermissionsSummaryCards'
import { AppPermissionsFilters, AppPermissionsFilterTags } from './AppPermissionsFilters'
import { AppPermissionsTable } from './AppPermissionsTable'
import { AppPermissionsDetailDrawer } from './AppPermissionsDetailDrawer'
import ConsentFeed from './ConsentFeed'
import type {
  AcknowledgeOAuthAppRequest,
  AcknowledgmentFilterStatus,
  ConsentRecordRow,
  OAuthApp,
  OAuthAppDetail,
  OAuthAppPermission,
  OAuthAppSummary,
  OAuthAppsPagedResponse,
  PermissionHeatmap,
} from './appPermissionsTypes'

const n = (v: number) => v.toLocaleString('en-US')

export interface AppPostureTabProps {
  tenantId: string | undefined
}

export function AppPostureTab({ tenantId }: AppPostureTabProps) {
  const queryClient = useQueryClient()
  const { accounts } = useMsal()
  const userEmail = accounts[0]?.username

  // Filter state — preserved verbatim from AppPermissionsTab.
  const [riskTier, setRiskTier] = useState<string | undefined>(undefined)
  const [appType, setAppType] = useState<string | undefined>('NonMicrosoft')
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [verified, setVerified] = useState<string | undefined>(undefined)
  const [acknowledgmentStatus, setAcknowledgmentStatus] =
    useState<AcknowledgmentFilterStatus | undefined>(undefined)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 20

  // Detail drawer state.
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Reset page when filters or tenant change.
  useEffect(() => {
    setCurrentPage(1)
  }, [tenantId, riskTier, appType, status, verified, acknowledgmentStatus, search])

  // Message Center deep-link handoff. Notifications for OAuth-app credential
  // expirations and review acknowledgments link to
  // `/security?tab=app-permissions&app=<id>` — on mount we open the drawer
  // for that app, then strip the consumed `app` param so a refresh doesn't
  // re-trigger. Mirrors the `?policy=` / `?workload=` pattern in
  // BestPracticeAnalyzerTab.tsx.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const appParam = searchParams.get('app')
    if (!appParam) return
    setSelectedAppId(appParam)
    setIsDrawerOpen(true)
    setSearchParams(
      prev => {
        prev.delete('app')
        return prev
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams])

  /** Acknowledgment workflow — HARD PRESERVE. */
  const handleAcknowledge = useCallback(
    async (appId: string, request: AcknowledgeOAuthAppRequest) => {
      if (!tenantId) return
      try {
        await api.acknowledgeOAuthApp(tenantId, appId, request)
        toast.success('App review saved.')
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['oauth-apps-summary'] }),
          queryClient.invalidateQueries({ queryKey: ['oauth-apps'] }),
          queryClient.invalidateQueries({ queryKey: ['oauth-app-detail', tenantId, selectedAppId] }),
        ])
      } catch {
        toast.error('The app review did not save. Try again.')
        throw new Error('Acknowledgment failed')
      }
    },
    [tenantId, selectedAppId, queryClient],
  )

  // Summary query — drives PillarBrief and the tier tabs.
  const { data: summary, isLoading: summaryLoading } = useQuery<OAuthAppSummary>({
    queryKey: ['oauth-apps-summary', tenantId],
    queryFn: () => api.getOAuthAppSummary(tenantId!),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      (query.state.data as OAuthAppSummary | undefined)?.syncInProgress ? 10_000 : false,
  })

  // Apps list query.
  const { data: appsResponse, isLoading: appsLoading } = useQuery<OAuthAppsPagedResponse>({
    queryKey: ['oauth-apps', tenantId, riskTier, appType, status, verified, search, currentPage],
    queryFn: () =>
      api.getOAuthApps(tenantId!, currentPage, pageSize, riskTier, appType, status, verified, search),
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // App detail query (fires when a row is clicked).
  const { data: appDetail, isLoading: detailLoading } = useQuery<OAuthAppDetail>({
    queryKey: ['oauth-app-detail', tenantId, selectedAppId],
    queryFn: () => api.getOAuthAppDetail(tenantId!, selectedAppId!),
    enabled: !!tenantId && !!selectedAppId && isDrawerOpen,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // Unfiltered apps query — drives the top-three tiles below. The
  // filter-aware query above varies with the user's filter state; the
  // top three must rank against the full estate, not the filtered slice.
  const { data: triageAppsResponse } = useQuery<OAuthAppsPagedResponse>({
    queryKey: ['oauth-apps', tenantId, 'triage-top'],
    queryFn: () =>
      api.getOAuthApps(
        tenantId!,
        1,
        50,
        undefined,
        'NonMicrosoft',
        undefined,
        undefined,
        undefined,
      ),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const handleAppClick = (app: OAuthApp) => {
    setSelectedAppId(app.id)
    setIsDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    setTimeout(() => setSelectedAppId(null), 300)
  }

  const handleClearAllFilters = () => {
    setRiskTier(undefined)
    setAppType(undefined)
    setStatus(undefined)
    setVerified(undefined)
    setAcknowledgmentStatus(undefined)
    setSearch('')
  }

  /* --------------------------------------------------------------------- */
  /* Top-three selection (Phase B §8.2)                                    */
  /* --------------------------------------------------------------------- */

  /**
   * Pick the three apps that best deserve a closer look. Sort by tier
   * (Critical=0, High=1, Medium=2, Low=3) ascending; tie-break on
   * `aiRiskScore` desc then `highPrivilegePermissionCount` desc. Filter out
   * apps the operator has already cleared with status='Approved' so the
   * top three surface real concerns rather than previously-acknowledged ones.
   */
  const topThreeApps = useMemo<OAuthApp[]>(() => {
    const all = triageAppsResponse?.items ?? []
    const tierRank: Record<OAuthApp['overallRiskTier'], number> = {
      Critical: 0,
      High: 1,
      Medium: 2,
      Low: 3,
    }
    const candidates = all.filter(
      (a) => (a.acknowledgmentStatus ?? '').toLowerCase() !== 'approved',
    )
    const sorted = [...candidates].sort((a, b) => {
      const tierDiff =
        (tierRank[a.overallRiskTier] ?? 99) - (tierRank[b.overallRiskTier] ?? 99)
      if (tierDiff !== 0) return tierDiff
      const aiDiff = (b.aiRiskScore ?? -1) - (a.aiRiskScore ?? -1)
      if (aiDiff !== 0) return aiDiff
      return (
        (b.highPrivilegePermissionCount ?? 0) -
        (a.highPrivilegePermissionCount ?? 0)
      )
    })
    return sorted.slice(0, 3)
  }, [triageAppsResponse])

  /* --------------------------------------------------------------------- */
  /* Phase B detail-fetch (top-three named scopes)                         */
  /* --------------------------------------------------------------------- */

  const triptychDetailQueries = useQueries({
    queries: topThreeApps.map((app) => ({
      queryKey: ['oauth-app-detail', tenantId, app.id],
      queryFn: () => api.getOAuthAppDetail(tenantId!, app.id),
      enabled: !!tenantId && topThreeApps.length > 0,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    })),
  })

  /* --------------------------------------------------------------------- */
  /* Phase C — heatmap + recent consents                                   */
  /* --------------------------------------------------------------------- */

  const hasNoData =
    !summaryLoading && (!summary || (summary?.totalApps ?? 0) === 0)

  const { data: heatmap, isLoading: heatmapLoading } = useQuery<PermissionHeatmap>({
    queryKey: ['oauth-permission-heatmap', tenantId],
    queryFn: () => api.getOAuthPermissionHeatmap(tenantId!),
    enabled: !!tenantId && !hasNoData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const { data: recentConsents, isLoading: consentsLoading } = useQuery<
    ConsentRecordRow[]
  >({
    queryKey: ['oauth-recent-consents', tenantId],
    queryFn: () => api.getOAuthRecentConsents(tenantId!, 10),
    enabled: !!tenantId && !hasNoData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  /* --------------------------------------------------------------------- */
  /* Composer-driven brief                                                 */
  /* --------------------------------------------------------------------- */

  /**
   * Estimate how many of the top-tier (Critical+High) apps come from
   * unverified, non-first-party publishers. Drives the brief's
   * unverified-publisher headline and verdict branches.
   *
   * `undefined` while `triageAppsResponse` hasn't loaded yet — an unknown
   * count, not a known zero. Coercing to `0` here would make the brief
   * confidently report "nothing to review" for a fraction of a second
   * before the real app list arrives.
   *
   * `triageAppsResponse` is also only the first page (50 rows) of
   * NonMicrosoft apps. When the tenant has more than one page, the filtered
   * count only reflects that first page, not the tenant's real total — also
   * an unknown, not the true count, so it stays `undefined` rather than
   * rendering a fabricated exact figure.
   */
  const unverifiedTier1Count = useMemo(() => {
    if (!triageAppsResponse) return undefined
    if (triageAppsResponse.items.length < triageAppsResponse.total) return undefined
    return triageAppsResponse.items.filter(
      (a) =>
        (a.overallRiskTier === 'Critical' || a.overallRiskTier === 'High') &&
        !a.isFirstParty &&
        a.isVerifiedPublisher === false,
    ).length
  }, [triageAppsResponse])

  const brief = useMemo(
    () =>
      composeAppPostureBrief({
        userEmail,
        tenantId,
        summary: summary ?? null,
        unverifiedTier1Count,
        newThisWeek: summary?.newAppsLast7Days ?? null,
      }),
    [summary, tenantId, userEmail, unverifiedTier1Count],
  )

  const total = summary?.totalApps ?? 0
  const tierComponents = summary
    ? [
        { label: 'Critical' as const, count: summary.criticalCount },
        { label: 'High' as const, count: summary.highCount },
        { label: 'Medium' as const, count: summary.mediumCount },
        { label: 'Low' as const, count: summary.lowCount },
      ]
    : null

  // Empty state — tenant not selected.
  if (!tenantId) {
    return <NoTenantState />
  }

  // Empty state — sync hasn't run yet.
  if (hasNoData) {
    return <NoSyncState />
  }

  const filterProps = {
    riskTier,
    appType,
    status,
    verified,
    acknowledgmentStatus,
    search,
    onRiskTierChange: setRiskTier,
    onAppTypeChange: setAppType,
    onStatusChange: setStatus,
    onVerifiedChange: setVerified,
    onAcknowledgmentStatusChange: setAcknowledgmentStatus,
    onSearchChange: setSearch,
    onClearAll: handleClearAllFilters,
  }

  // Microsoft's own apps are left out whenever the type filter is set to anything but Microsoft.
  const notices: ReactNode[] = []
  if (summary && summary.microsoftAppCount > 0 && appType !== undefined && appType !== 'Microsoft') {
    notices.push(
      <InlineNotification
        key="microsoft"
        kind="info"
        title={`${n(summary.microsoftAppCount)} Microsoft first-party apps are hidden.`}
        actions={
          <Button kind="ghost" size="sm" onClick={() => setAppType('Microsoft')}>
            Show them
          </Button>
        }
      />,
    )
  }
  // The default "Non-Microsoft" view is not a tag: the notice above already says what it leaves out.
  const hasFilterTags =
    !!riskTier || (!!appType && appType !== 'NonMicrosoft') || !!status || !!verified || !!acknowledgmentStatus || !!search

  return (
    <div>
      <PillarBrief
        brief={brief}
        score={total}
        components={[]}
        tone="bpa"
        isLoading={summaryLoading}
        // The composer's provenance line carries the last sync; no second time segment.
        aside={<InventoryAside total={total} tiers={tierComponents} isLoading={summaryLoading} />}
        footerNote="Findings on this page roll up into **Best Practice**. App Posture is inventory, not a separate Guardian score, and Guardian syncs it twice a day."
      />

      {summary?.syncInProgress ? (
        <InlineNotification kind="info" title="Syncing the app inventory." className="mt-6">
          The counts on this page update when the sync finishes.
        </InlineNotification>
      ) : null}

      {/* Phase B §8.2 — Three apps to review first. */}
      <TopThreeApps
        apps={topThreeApps}
        details={triptychDetailQueries.map((q) => q.data)}
        onAppClick={handleAppClick}
      />

      {/* Phase C §8.3 + §8.4 — Permission heatmap + publisher trust. */}
      <PermissionHeatmapSection
        heatmap={heatmap}
        isLoading={heatmapLoading}
        summary={summary}
      />

      {/* Phase C §8.5 — Recent consents feed. */}
      <RecentConsentsSection
        rows={recentConsents}
        isLoading={consentsLoading}
      />

      <OAuthEducationSection />

      <div className="mt-12">
        <AppPermissionsTable
          id="app-inventory"
          title="App inventory"
          description={summary ? <InventoryDescription summary={summary} /> : 'Filters narrow the list. Open any app for its full permission detail.'}
          notices={notices.length > 0 ? notices : undefined}
          filterTags={hasFilterTags ? <AppPermissionsFilterTags {...filterProps} /> : undefined}
          tabs={
            <AppPermissionsSummaryCards
              summary={summary}
              isLoading={summaryLoading}
              onFilterClick={setRiskTier}
              activeFilter={riskTier}
            />
          }
          toolbar={<AppPermissionsFilters {...filterProps} />}
          apps={appsResponse?.items ?? []}
          total={appsResponse?.total ?? 0}
          pageNumber={appsResponse?.pageNumber ?? currentPage}
          pageSize={appsResponse?.pageSize ?? pageSize}
          totalPages={appsResponse?.totalPages ?? 0}
          isLoading={appsLoading}
          onPageChange={setCurrentPage}
          onAppClick={handleAppClick}
        />
      </div>

      <AppPermissionsDetailDrawer
        app={appDetail ?? null}
        isOpen={isDrawerOpen}
        isLoading={detailLoading}
        onClose={handleCloseDrawer}
        onAcknowledge={handleAcknowledge}
      />
    </div>
  )
}

export default AppPostureTab

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

/** A section's heading and its one-line description (DESIGN.md "Heading", no kicker). */
function SectionHead({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-4">
      <SectionHeadline level="page">{title}</SectionHeadline>
      {description ? <p className="m-0 mt-0.5 max-w-[72ch] text-body text-text-secondary">{description}</p> : null}
    </div>
  )
}

/** The App inventory table's description: the inventory size and the stale count, from the summary. */
function InventoryDescription({ summary }: { summary: OAuthAppSummary }) {
  return (
    <>
      <b className="font-semibold tabular-nums text-text-primary">{n(summary.totalApps)}</b>{' '}
      {summary.totalApps === 1 ? 'app' : 'apps'} from publishers other than Microsoft
      {summary.staleCount > 0 ? (
        <>
          {' '}
          · <span className="tabular-nums">{n(summary.staleCount)}</span> with no sign-in in 90 days
        </>
      ) : null}
      . Open any app for its full permission detail.
    </>
  )
}

/**
 * The tile beside the App Posture brief, in place of a score tile: App
 * Posture is inventory, not a Guardian-score pillar, so a 0-100 tile would
 * falsely signal that this page feeds the composite. It shows the app count
 * and the risk tiers as a proportional severity meter.
 */
function InventoryAside({
  total,
  tiers,
  isLoading,
}: {
  total: number
  tiers: { label: 'Critical' | 'High' | 'Medium' | 'Low'; count: number }[] | null
  isLoading: boolean
}) {
  return (
    <Tile
      as="aside"
      padding={24}
      aria-labelledby="app-inventory-tile-title"
      aria-busy={isLoading || undefined}
      className="flex flex-col self-start max-c-lg:max-w-[640px]"
    >
      <TileHeader title="Apps in tenant" level={2} id="app-inventory-tile-title" />
      {isLoading || !tiers ? (
        <Skeleton lines={3} label="Loading the app inventory" className="mt-4" />
      ) : (
        <>
          <BigNumber value={total} size="score" className="mt-4" />
          <p className="m-0 mt-1 text-helper text-text-helper">from publishers other than Microsoft</p>
          <div className="mt-6">
            <TierStackedBar tiers={tiers} total={total} />
          </div>
        </>
      )}
    </Tile>
  )
}

function NoTenantState() {
  return (
    <EmptyState
      headline="Choose a tenant to see its OAuth app posture."
      body="Pick one in the tenant switcher. Guardian lists every app that holds permissions in it."
      headingLevel="h1"
      className="mt-8"
    />
  )
}

function NoSyncState() {
  return (
    <EmptyState
      headline="Guardian hasn't discovered any OAuth apps yet."
      body="Apps appear here after the first scheduled sync. Guardian syncs the inventory twice a day."
      headingLevel="h1"
      className="mt-8"
    />
  )
}

const EDUCATION_DISMISSED_KEY = 'guardian-oauth-education-dismissed'

/** Always-available guide: open by default, dismissible, remembered per browser. */
function OAuthEducationSection() {
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem(EDUCATION_DISMISSED_KEY) === 'true'
    } catch {
      return false
    }
  })

  const handleDismiss = () => {
    setIsDismissed(true)
    try {
      localStorage.setItem(EDUCATION_DISMISSED_KEY, 'true')
    } catch {
      /* noop */
    }
  }

  const handleShow = () => {
    setIsDismissed(false)
    try {
      localStorage.removeItem(EDUCATION_DISMISSED_KEY)
    } catch {
      /* noop */
    }
  }

  if (isDismissed) {
    return (
      <div className="mt-12">
        <Button kind="ghost" size="sm" onClick={handleShow} icon={<IconInfo />} className="-ml-3">
          Show the OAuth posture guide
        </Button>
      </div>
    )
  }

  return (
    <Tile as="section" aria-labelledby="oauth-guide-title" padding={24} className="relative mt-12">
      <IconButton label="Hide the guide" icon={<IconClose />} onClick={handleDismiss} className="absolute right-0 top-0" />
      <h2 id="oauth-guide-title" className="m-0 pr-12 text-heading font-normal text-text-primary">
        Reading OAuth app posture
      </h2>
      <p className="m-0 mt-2 max-w-[72ch] text-body text-text-secondary">
        Enterprise applications in your Microsoft 365 tenant hold permissions to read, change or delete organizational
        data. Posture here means three things: how broad each app&rsquo;s scopes are, whether the publisher is
        verified, and whether the app is still in use.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-8 c-md:grid-cols-2">
        <div className="min-w-0">
          <h3 className="m-0 text-body font-semibold leading-[1.29] text-text-primary">Why this matters</h3>
          <ul className="m-0 mt-2 grid list-disc gap-1.5 pl-5 text-body text-text-secondary marker:text-text-helper">
            <li>Over-privileged apps widen the blast radius if one is compromised.</li>
            <li>Unverified third-party apps may not follow security best practices.</li>
            <li>Stale apps with active credentials are an unmonitored way in.</li>
            <li>Apps with org-wide access affect every user, not just the people who consented.</li>
          </ul>
        </div>
        <div className="min-w-0">
          <h3 className="m-0 text-body font-semibold leading-[1.29] text-text-primary">What to look for</h3>
          <ul className="m-0 mt-2 grid list-disc gap-1.5 pl-5 text-body text-text-secondary marker:text-text-helper">
            <li>Critical or High tier: review the scopes and hold them to least privilege.</li>
            <li>Directory roles: these carry implicit admin access.</li>
            <li>Unverified publishers: confirm the vendor&rsquo;s identity and posture.</li>
            <li>No owner: assign one so someone is accountable.</li>
            <li>Stale: disable or remove apps that haven&rsquo;t signed in recently.</li>
            <li>Secret-based credentials: prefer a certificate or federated identity.</li>
          </ul>
        </div>
      </div>

      <p className="m-0 mt-6 text-helper text-text-helper">
        Review an app from its detail panel to record a decision and keep an audit trail.
      </p>
    </Tile>
  )
}

/* -------------------------------------------------------------------------- */
/* Phase B §8.2 — Three apps to review first                                   */
/* -------------------------------------------------------------------------- */

const ACTION_LABEL: Record<NonNullable<OAuthApp['aiRecommendationAction']>, string> = {
  REVOKE: 'Revoke',
  REDUCE: 'Reduce scopes',
  REVIEW: 'Review',
  ACCEPT: 'Approve',
}

function TopThreeApps({
  apps,
  details,
  onAppClick,
}: {
  apps: OAuthApp[]
  details: (OAuthAppDetail | undefined)[]
  onAppClick: (app: OAuthApp) => void
}) {
  if (apps.length === 0) {
    return (
      <section className="mt-12">
        <SectionHead title="Apps to review first" />
        <Tile>
          <p className="m-0 text-body text-text-secondary">Nothing to review. No unapproved app needs a closer look.</p>
        </Tile>
      </section>
    )
  }

  return (
    <section className="mt-12">
      <SectionHead
        title={apps.length === 1 ? 'The app to review first' : `${apps.length === 3 ? 'Three' : 'Two'} apps to review first`}
        description="Ranked by risk tier, then Guardian's risk score, then the count of privileged scopes. Apps already approved are left out."
      />
      <CondensedGrid as="ol" columns={{ base: 1, 'c-md': 2, 'c-xlg': 3 }}>
        {apps.map((app, idx) => (
          <TopAppTile key={app.id} app={app} detail={details[idx]} onClick={() => onAppClick(app)} />
        ))}
      </CondensedGrid>
    </section>
  )
}

function TopAppTile({
  app,
  detail,
  onClick,
}: {
  app: OAuthApp
  detail: OAuthAppDetail | undefined
  onClick: () => void
}) {
  const why = whyItMatters(app)
  const publisher = app.publisherDomain ?? app.publisherName ?? 'Unknown publisher'
  const trust = app.isFirstParty ? 'First party' : app.isVerifiedPublisher ? 'Verified publisher' : 'Unverified publisher'

  // Pull the top three named scopes from the detail fetch, highest tier first.
  const topScopes = useMemo<OAuthAppPermission[]>(() => {
    if (!detail) return []
    const tierRank: Record<OAuthAppPermission['riskTier'], number> = {
      Critical: 0,
      High: 1,
      Medium: 2,
      Low: 3,
    }
    const all = [...detail.applicationPermissions, ...detail.delegatedPermissions]
    return [...all]
      .sort(
        (a, b) =>
          (tierRank[a.riskTier] ?? 99) - (tierRank[b.riskTier] ?? 99),
      )
      .slice(0, 3)
  }, [detail])

  return (
    <Tile as="li" className="flex min-w-0 flex-col pb-2">
      <TileHeader
        title={<span className="break-words">{app.displayName}</span>}
        level={3}
        aside={
          <SeverityGlyph level={severityFromApi(app.overallRiskTier)} size={12} className="text-helper">
            {app.overallRiskTier}
          </SeverityGlyph>
        }
      />
      <p className="m-0 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-helper text-text-helper">
        <span className="min-w-0 break-all">{publisher}</span>
        <Tag size="sm" kind={app.isFirstParty || app.isVerifiedPublisher ? 'default' : 'outline'}>
          {trust}
        </Tag>
      </p>

      <p className="m-0 mt-4 text-body text-text-secondary">
        <Marked text={why} />
      </p>

      <div className="mt-4">
        <p className="m-0 mb-2 text-helper text-text-helper">Top scopes</p>
        {detail ? (
          topScopes.length === 0 ? (
            <p className="m-0 text-helper text-text-helper">No granted permissions on record.</p>
          ) : (
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {topScopes.map((p) => (
                <li key={p.id} className="min-w-0 max-w-full">
                  <Tag kind="outline" title={p.permissionDescription ?? p.permissionName} className="font-mono">
                    {p.permissionName}
                  </Tag>
                </li>
              ))}
            </ul>
          )
        ) : (
          <Skeleton width="60%" label="Loading scopes" />
        )}
      </div>

      {app.aiRecommendationAction ? (
        <p className="m-0 mt-4 flex flex-wrap items-center gap-2 text-helper text-text-helper">
          Guardian suggests <Tag>{ACTION_LABEL[app.aiRecommendationAction]}</Tag>
        </p>
      ) : null}

      <div className="mt-auto pt-4">
        <Button kind="ghost" onClick={onClick} icon={<IconArrowRight />} className="-ml-4">
          View details<span className="sr-only"> for {app.displayName}</span>
        </Button>
      </div>
    </Tile>
  )
}

/* -------------------------------------------------------------------------- */
/* Phase C §8.3 + §8.4 — Permission heatmap + publisher trust                  */
/* -------------------------------------------------------------------------- */

const HEATMAP_OPS = ['Read', 'ReadWrite', 'Manage', 'SendAct'] as const
const HEATMAP_SCOPES = ['User', 'Group', 'Tenant', 'ServicePrincipal'] as const

const HEATMAP_OP_LABELS: Record<(typeof HEATMAP_OPS)[number], string> = {
  Read: 'Read',
  ReadWrite: 'Read+Write',
  Manage: 'Manage',
  SendAct: 'Send / Act',
}

const HEATMAP_SCOPE_LABELS: Record<(typeof HEATMAP_SCOPES)[number], string> = {
  User: 'User',
  Group: 'Group',
  Tenant: 'Tenant-wide',
  ServicePrincipal: 'Service Principal',
}

function intensityForHeatmap(
  count: number,
  opIsManage: boolean,
  scopeIsTenant: boolean,
): Intensity {
  if (count === 0) return 'l0'
  if (count <= 2) return 'l1'
  if (count <= 5) return 'l2'
  if (count <= 10) return opIsManage || scopeIsTenant ? 'l4' : 'l3'
  return 'l4'
}

function PermissionHeatmapSection({
  heatmap,
  isLoading,
  summary,
}: {
  heatmap: PermissionHeatmap | undefined
  isLoading: boolean
  summary: OAuthAppSummary | undefined
}) {
  const heatmapRows = useMemo<HeatmapRow[]>(() => {
    if (!heatmap?.grid) return []
    return HEATMAP_OPS.map((op) => {
      const opIsManage = op === 'Manage' || op === 'SendAct'
      const cells = HEATMAP_SCOPES.map((scope) => {
        const count = heatmap.grid?.[op]?.[scope] ?? 0
        const scopeIsTenant = scope === 'Tenant'
        const intensity = intensityForHeatmap(count, opIsManage, scopeIsTenant)
        return {
          value: count,
          intensity,
          crit: opIsManage && scopeIsTenant && count > 0,
        }
      })
      return {
        label: HEATMAP_OP_LABELS[op],
        cells,
      }
    })
  }, [heatmap])

  return (
    <section className="mt-12">
      <SectionHead
        title="Where permissions concentrate"
        description="Distinct apps holding at least one permission in each operation and scope. Manage and tenant-wide cells carry the most risk."
      />
      <CondensedGrid columns={{ base: 1, 'c-lg': 3 }}>
        <Tile className="min-w-0 c-lg:col-span-2">
          <TileHeader title="Apps by operation and scope" level={3} />
          <div className="mt-4 min-w-0 overflow-x-auto">
            {isLoading ? (
              <Skeleton lines={4} label="Loading the permission grid" />
            ) : heatmapRows.length === 0 ? (
              <p className="m-0 text-body text-text-secondary">No classifiable permissions found yet.</p>
            ) : (
              <EditorialHeatmap
                rows={heatmapRows}
                columns={HEATMAP_SCOPES.map((s) => HEATMAP_SCOPE_LABELS[s])}
                labelWidth={140}
              />
            )}
          </div>
        </Tile>
        <PublisherTrust summary={summary} />
      </CondensedGrid>
    </section>
  )
}

/**
 * Publisher trust as a proportional meter (Unverified in the strong neutral,
 * Verified in secondary gray, Microsoft first party in the unearned gray),
 * then the admin-consent count. When the backend hasn't backfilled
 * `adminConsentedCount`, the stale count stands in, relabelled, so the slot
 * still carries a real figure.
 */
function PublisherTrust({ summary }: { summary: OAuthAppSummary | undefined }) {
  const haveData =
    !!summary &&
    typeof summary.unverifiedPublisherCount === 'number' &&
    typeof summary.firstPartyCount === 'number'

  // Backend `totalApps` already excludes first-party Microsoft apps, so the
  // total is non-first-party + first-party, and `verified` is the
  // non-first-party remainder once unverified is taken out.
  const nonFirstParty = summary?.totalApps ?? 0
  const unverified = summary?.unverifiedPublisherCount ?? 0
  const firstParty = summary?.firstPartyCount ?? 0
  const verified = Math.max(0, nonFirstParty - unverified)
  const chartTotal = nonFirstParty + firstParty

  const hasAdmin = typeof summary?.adminConsentedCount === 'number'

  return (
    <Tile className="flex min-w-0 flex-col">
      <TileHeader title="Publisher trust" level={3} />
      <div className="mt-4">
        {!haveData ? (
          <p className="m-0 text-body text-text-secondary">Publisher composition isn&rsquo;t available yet.</p>
        ) : chartTotal === 0 ? (
          <p className="m-0 text-body text-text-secondary">No apps to chart yet.</p>
        ) : (
          <SegmentedMeter
            caption={`Across ${n(chartTotal)} ${chartTotal === 1 ? 'app' : 'apps'}`}
            segments={[
              { id: 'unverified', value: unverified, tone: 'strong', label: 'unverified' },
              { id: 'verified', value: verified, tone: 'med', label: 'verified' },
              { id: 'first-party', value: firstParty, tone: 'off', label: 'Microsoft first party' },
            ]}
          />
        )}
      </div>
      {summary ? (
        <div className="mt-6 border-t border-line-subtle pt-4">
          <p className="m-0 text-body font-semibold leading-[1.29] text-text-primary">
            {hasAdmin ? 'Admin consented' : 'Stale apps'}
          </p>
          <BigNumber value={hasAdmin ? summary.adminConsentedCount! : summary.staleCount} size="figure" className="mt-2" />
          <p className="m-0 mt-0.5 text-helper text-text-helper">
            {hasAdmin ? 'Granted by an admin on behalf of the tenant' : 'No sign-in in the last 90 days'}
          </p>
        </div>
      ) : null}
    </Tile>
  )
}

/* -------------------------------------------------------------------------- */
/* Phase C §8.5 — Recent consents                                              */
/* -------------------------------------------------------------------------- */

function RecentConsentsSection({
  rows,
  isLoading,
}: {
  rows: ConsentRecordRow[] | undefined
  isLoading: boolean
}) {
  return (
    <section className="mt-12">
      <SectionHead title="Recent consents" description="Who granted which app what, from the consent grants Guardian captured." />
      <ConsentFeed rows={rows} isLoading={isLoading} />
    </section>
  )
}
